import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styled, { withTheme } from 'styled-components';
import {
  VictoryArea,
  VictoryAxis,
  VictoryChart,
  VictoryLabel,
  VictoryLine,
  VictoryTooltip,
  VictoryVoronoiContainer,
} from 'victory';

import { getDateLabel } from '../../../utils/dateLabel';
import LoadingCard from '../../LoadingCard';
import { NavArrowButton } from '../../Styled';
import StyledTitle from '../../Styled/StyledTitle';

const ChartHeaderNav = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const BatteryStateOfCharge = ({
  data,
  fetchingAnalyticsData,
  theme,
  onPreviousDay,
  onNextDay,
  isAtMinDate,
  isAtMaxDate,
  selectedDate,
}) => {
  const intersectionX = data && data[data?.length - 1]?.x;
  const dataAvailable = data?.length;
  const dateLabel = getDateLabel(selectedDate);

  const placeholderData = [
    { time: '00:00', earnings: 0 },
    { time: '03:00', earnings: 0 },
    { time: '06:00', earnings: 0 },
    { time: '09:00', earnings: 0 },
    { time: '12:00', earnings: 0 },
    { time: '15:00', earnings: 0 },
    { time: '18:00', earnings: 0 },
    { time: '21:00', earnings: 0 },
    { time: '23:00', earnings: 0 },
  ];

  const yDomain = dataAvailable ? undefined : [0, 100];

  return (
    <>
      {/* Header section with navigation arrows and title */}
      <ChartHeaderNav style={{ marginTop: 60, marginBottom: 0 }}>
        <NavArrowButton
          onClick={onPreviousDay}
          disabled={isAtMinDate || fetchingAnalyticsData}
          aria-label="Previous day"
        >
          <FontAwesomeIcon icon={faChevronLeft} />
        </NavArrowButton>

        <StyledTitle>Battery state of charge {dateLabel}</StyledTitle>

        <NavArrowButton onClick={onNextDay} disabled={isAtMaxDate || fetchingAnalyticsData} aria-label="Next day">
          <FontAwesomeIcon icon={faChevronRight} />
        </NavArrowButton>
      </ChartHeaderNav>
      {fetchingAnalyticsData && (
        <div className="mt-4">
          <LoadingCard />
        </div>
      )}
      {!fetchingAnalyticsData > 0 && (
        <div>
          <VictoryChart
            width={648}
            height={350}
            domain={{ y: yDomain }}
            padding={{ top: 30, right: 50, bottom: 50, left: 65 }}
            containerComponent={
              <VictoryVoronoiContainer
                voronoiBlacklist={!data?.length && ['socChart', 'boundaryLine']}
                voronoiDimension="x"
                labels={({ datum }) => `Time: ${datum?.x}\nState of charge: ${datum.y}%`}
                labelComponent={
                  <VictoryTooltip
                    constrainToVisibleArea={true}
                    cornerRadius={2}
                    pointerLength={4}
                    style={{ fontSize: 16 }}
                    flyoutPadding={{ top: 8, bottom: 8, left: 8, right: 8 }}
                    flyoutStyle={{
                      fill: theme.colors.white,
                      stroke: theme.colors.grey,
                    }}
                  />
                }
              />
            }
          >
            {!dataAvailable && (
              <VictoryLine
                name="boundaryLine"
                style={{
                  data: { stroke: 'grey', strokeWidth: 1 },
                }}
                y={() => 100}
              />
            )}
            <VictoryAxis
              dependentAxis
              tickValues={[0, 25, 50, 75, 100]}
              label="Battery state of charge (%)"
              style={{
                ticks: { stroke: 'transparent' },
                tickLabels: {
                  fill: theme.colors.black,
                  fontSize: 14,
                  padding: 5,
                },
                axis: { stroke: 'transparent' },
                axisLabel: {
                  fontSize: 17,
                  padding: 35,
                  textAnchor: 'middle',
                  fill: theme.colors.black,
                  fontWeight: 'normal',
                },
                grid: {
                  stroke: dataAvailable ? theme.colors.grey : theme.colors.white,
                  strokeWidth: 0.7,
                  strokeDasharray: '0',
                },
              }}
            />
            <VictoryAxis
              tickValues={dataAvailable ? data.map((d) => d?.x) : placeholderData.map((d) => d?.time)}
              label="Half-hourly time period"
              tickFormat={(t, index, ticks) => {
                if (
                  index === 0 ||
                  index === ticks.length - 1 ||
                  ['06:00', '12:00', '18:00'].includes(t) ||
                  t === intersectionX
                ) {
                  return t;
                }
                return '';
              }}
              style={{
                ticks: { stroke: theme.colors.light, size: 5 },
                axis: { stroke: theme.colors.light, strokeWidth: 2 },
                tickLabels: {
                  fill: theme.colors.black,
                  fontSize: 14,
                  padding: 5,
                },
                axisLabel: {
                  fontSize: 17,
                  padding: 30,
                  textAnchor: 'middle',
                  fill: theme.colors.black,
                  fontWeight: 'normal',
                },
              }}
            />
            <VictoryArea
              name="socChart"
              data={dataAvailable ? data : placeholderData}
              style={{
                data: {
                  fill: theme.colors.secondary,
                  fillOpacity: 0.1,
                  stroke: theme.colors.secondary,
                  strokeWidth: 3,
                },
              }}
            />
            {!dataAvailable && (
              <>
                <VictoryLabel
                  textAnchor="middle"
                  style={{ fontSize: 15, fontWeight: 'bold' }}
                  x={'50%'}
                  y={175}
                  text="Not enough data to show Battery state of charge."
                />
              </>
            )}
          </VictoryChart>
        </div>
      )}
    </>
  );
};

export default withTheme(BatteryStateOfCharge);
